import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, Pagination, TableSkeleton} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';

import BusinessRFIFilters from './BusinessRFIFilters';
import BusinessRFITable from './BusinessRFITable';
import SendBusinessRFIModal from './SendBusinessRFIModal';
import {useBusinessRFIList} from './useBusinessRFIList';

const BusinessRFITab = ({
  businessId,
  businessName,
  dataCy,
}: {
  businessId: string;
  businessName: string;
  dataCy?: string;
}) => {
  const {
    list,
    pageInfo,
    isLoading,
    isPageReset,
    fetchList,
    currentFilters,
    selectCurrentFilters,
    selectAppliedFilters,
    handleApplyFilters,
    clearFilters,
  } = useBusinessRFIList(businessId);

  const [showCreate, setShowCreate] = useState(false);

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list.length) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
          No RFIs found for this business.
        </p>
      );
    }

    return (
      <BusinessRFITable
        dataCy={dataCy}
        items={list}
        businessName={businessName}
      />
    );
  };

  return (
    <section className="px-3 pt-3">
      <BusinessRFIFilters
        dataCy={dataCy}
        currentFilters={currentFilters}
        selectCurrentFilters={selectCurrentFilters}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
        clearFilters={clearFilters}
        extraAction={
          <PermissionsProvider permission={AfriexAdminPermission.RFIManage}>
            <Button
              dataCy={dataCy ? `${dataCy}-create-rfi` : undefined}
              onClick={() => setShowCreate(true)}
            >
              Create RFI +
            </Button>
          </PermissionsProvider>
        }
      />

      {renderList()}

      {list.length ? (
        <Pagination
          onPageChange={fetchList}
          total={pageInfo.total}
          isPageReset={isPageReset}
        />
      ) : null}

      {showCreate && (
        <SendBusinessRFIModal
          dataCy={dataCy}
          businessId={businessId}
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </section>
  );
};

export default BusinessRFITab;
